import { Capacitor } from "@capacitor/core";
import { reverseGeocodeSecure } from "./googleMapsBackendService";

const reverseGeocodeCache = new Map();

function component(components, type) {
  return components.find(item => item.types?.includes(type));
}

export function formatGoogleAddress(components = [], formattedAddress = "") {
  const pointOfInterest = component(components, "point_of_interest")?.long_name
    || component(components, "establishment")?.long_name
    || component(components, "premise")?.long_name;
  const route = component(components, "route")?.long_name;
  const number = component(components, "street_number")?.long_name;
  const neighborhood = component(components, "sublocality_level_1")?.long_name
    || component(components, "sublocality")?.long_name
    || component(components, "neighborhood")?.long_name;
  const city = component(components, "locality")?.long_name
    || component(components, "administrative_area_level_2")?.long_name;
  const stateComponent = component(components, "administrative_area_level_1");
  const state = stateComponent?.short_name || stateComponent?.long_name;
  const formattedFallback = formattedAddress.split(",")[0]?.trim();
  const street = route ? `${route}${number ? `, ${number}` : ""}` : "";
  const primary = pointOfInterest || street || neighborhood || city || formattedFallback || "";
  const secondary = [city, state].filter(Boolean).join(" - ");

  return {
    primary,
    secondary,
    singleLine: [primary, street, secondary].filter((value, index, values) => value && values.indexOf(value) === index).join(" — "),
    city: city || "",
    state: state || "",
    formattedAddress,
    pointOfInterest: pointOfInterest || "",
    street,
  };
}

function formatNominatimAddress(data) {
  const address = data?.address || {};
  const route = address.road || address.pedestrian;
  const number = address.house_number;
  const neighborhood = address.suburb || address.city_district || address.neighbourhood;
  const city = address.city || address.town || address.village || address.municipality;
  const state = address["ISO3166-2-lvl4"]?.split("-").pop() || address.state_code || address.state;
  const pointOfInterest = data?.name && data.name !== route ? data.name : "";
  const street = route ? `${route}${number ? `, ${number}` : ""}` : "";
  const primary = pointOfInterest || street || neighborhood || city || data?.display_name?.split(",")[0]?.trim() || "";
  const secondary = [city, state].filter(Boolean).join(" - ");
  return { primary, secondary, singleLine: [primary, street, secondary].filter((value, index, values) => value && values.indexOf(value) === index).join(" — "), city: city || "", state: state || "", formattedAddress: data?.display_name || "", pointOfInterest, street };
}

function bestGoogleResult(results) {
  return results?.find(result => result.address_components?.some(item => item.types?.includes("street_number")))
    || results?.find(result => result.address_components?.some(item => item.types?.includes("route")))
    || results?.[0]
    || null;
}

async function reverseWithGoogleMaps(lat, lng) {
  if (!window.google?.maps?.Geocoder) return null;
  const results = await new Promise(resolve => {
    new window.google.maps.Geocoder().geocode(
      { location: { lat, lng } },
      (items, status) => resolve(status === "OK" ? items : null),
    );
  });
  const best = bestGoogleResult(results);
  return best ? formatGoogleAddress(best.address_components || [], best.formatted_address || "") : null;
}

async function reverseWithGoogleRest(lat, lng) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;
  if (!apiKey) return null;
  const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}&language=pt-BR`);
  const data = await response.json();
  const best = data.status === "OK" ? bestGoogleResult(data.results) : null;
  return best ? formatGoogleAddress(best.address_components || [], best.formatted_address || "") : null;
}

async function reverseWithNominatim(lat, lng) {
  const response = await fetch(
    `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=pt`,
    { headers: { Accept: "application/json", "User-Agent": "ZunoMovel/1.0" } },
  );
  const data = await response.json();
  return data?.error ? null : formatNominatimAddress(data);
}

export async function reverseGeocodeForPlatform(lat, lng, platform = Capacitor.getPlatform(), backend = reverseGeocodeSecure) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const cacheKey = `${lat.toFixed(4)},${lng.toFixed(4)}`;
  if (reverseGeocodeCache.has(cacheKey)) return reverseGeocodeCache.get(cacheKey);

  let address = null;
  if (platform === "ios") {
    try { address = (await backend(lat, lng))?.address || null; } catch {}
    if (address?.primary) reverseGeocodeCache.set(cacheKey, address);
    return address?.primary ? address : null;
  }
  try { address = await reverseWithGoogleMaps(lat, lng); } catch {}
  try { if (!address?.primary) address = await reverseWithGoogleRest(lat, lng); } catch {}
  try { if (!address?.primary) address = await reverseWithNominatim(lat, lng); } catch {}

  if (address?.primary) reverseGeocodeCache.set(cacheKey, address);
  return address?.primary ? address : null;
}

export const reverseGeocode = (lat, lng) => reverseGeocodeForPlatform(lat, lng);

export function distanceInMeters(first, second) {
  if (!first || !second) return Infinity;
  const earthRadius = 6371000;
  const toRadians = value => value * Math.PI / 180;
  const deltaLat = toRadians(second.lat - first.lat);
  const deltaLng = toRadians(second.lng - first.lng);
  const a = Math.sin(deltaLat / 2) ** 2
    + Math.cos(toRadians(first.lat)) * Math.cos(toRadians(second.lat)) * Math.sin(deltaLng / 2) ** 2;
  return earthRadius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
